import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {style} from "typestyle";
import {
    classNames,
    Keyboard,
    KeyboardLayout,
    KeyPad,
    layout,
    PushButton,
    StringsOrTFunc,
    TextInput,
    toT,
    typography,
    useAsyncEffect,
    useMounted
} from "@variocube/cube-ui";

interface KeyboardInput {
    type: "keyboard";
    layout?: KeyboardLayout;
}

interface KeypadInput {
    type: "keypad";
    digits?: string[][];
}

type Input = KeyboardInput | KeypadInput;

interface CodeFormProps {
    length?: number;
    onVerify: (code: string) => Promise<void>;
    onCancel?: () => void;
    strings: StringsOrTFunc<
        | "InvalidCode"
        | "Submit"
        | "Cancel"
        | "CheckingCode"
        | "MoreDigitsRequired"
        | "PleaseEnterCode"
    >;
    className?: string;
    input?: Input;
    revealInput?: boolean;
}

export function CodeForm(props: CodeFormProps) {
    const {
        length,
        onVerify,
        onCancel,
        strings,
        className,
        input = {type: "keypad"},
        revealInput = false,
    } = props;

    const mounted = useMounted();
    const t = toT(strings);

    const [code, setCode] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<any>();

    const verify = useCallback(async () => {
        try {
            setPending(true);
            await onVerify(code);
            if (mounted()) {
                setError(undefined);
                setPending(false);
            }
        } catch (error) {
            if (mounted()) {
                setError(error);
                setPending(false);
                setCode("");
            }
        }
    }, [onVerify, code]);

    const handleType = useCallback((key: string) => setCode(prev => prev + key), []);

    useAsyncEffect(async () => {
        if (length && length == code.length) {
            await verify();
        }
    }, [length, code, verify]);

    useEffect(() => {
        if (code) {
            setError(undefined);
        }
    }, [code]);

    const {message, className: messageClassName} = useMemo(() => {
        if (error) {
            return {
                message: t("InvalidCode"),
                className: typography.colorError,
            };
        } else if (pending) {
            return {
                message: t("CheckingCode"),
                className: undefined,
            };
        } else if (length && code.length > 0 && code.length < length) {
            return {
                message: t("MoreDigitsRequired"),
                className: typography.muted,
            };
        } else {
            return {
                message: t("PleaseEnterCode"),
                className: typography.muted,
            };
        }
    }, [error, pending, length, code, strings]);

    return (
        <div
            className={classNames(input.type == "keyboard" ? layout.vertical : layout.horizontal, layout.spaceEvenly, className)}>
            <div
                className={classNames(input.type == "keyboard" ? layout.horizontal : layout.vertical, layout.center, typography.alignCenter)}>

                <div className={classNames(layout.vertical, codeInputWidth)}>
                    <TextInput
                        value={code}
                        onChange={setCode}
                        type={revealInput ? "text" : "password"}
                        disabled={pending}
                        outline
                    />
                    <p className={messageClassName}>{message}</p>
                </div>

                {!length && (
                    <div className={button}>
                        <PushButton primary onClick={verify}>
                            {t("Submit") + " (#)"}
                        </PushButton>
                    </div>
                )}

                {onCancel && (
                    <div className={button}>
                        <PushButton onClick={onCancel}>
                            {t("Cancel")}
                        </PushButton>
                    </div>
                )}
            </div>

            {input.type == "keypad" && (
                <div className={classNames(layout.vertical, layout.center)}>
                    <KeyPad primary round size={2.5} onType={handleType} disabled={pending} digits={input.digits}/>
                </div>
            )}

            {input.type == "keyboard" && (
                <Keyboard layout={input.layout} onType={handleType} disabled={pending}/>
            )}
        </div>
    );
}

const codeInputWidth = style({
    width: "20em",
});

const button = style({
    margin: "0.5em",
    textAlign: "center",
});